import React, { useEffect, useState } from "react";
import "./CartOrderDetails.scss";
import { Typography, Button } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { ShippingSelect } from "../../../../components";
import { useDispatch, useSelector } from "react-redux";
import {
  patchPatientShippingAddress,
  postPatientShippingAddress,
  getPatientDetails,
} from "../../../../features/patient/patientActions";
import { resetPatientError } from "../../../../features/patient/patientSlice";
import Utils from "../../../../utils";

export default function ShippingAddress({
  token,
  openNotificationWithIcon,
  addressError,
  selectedShippingAddress,
  setSelectedShippingAddress,
  set_pick_up_point_opened,
}) {
  const {
    patient,
    shipping_addresses: { results: shipping_addresses },
    loading,
    error,
  } = useSelector((state) => state?.patient);
  const dispatch = useDispatch();

  const [isOnEditMode, setIsOnEditMode] = useState(false);
  const [initialAddress, setInitialAddress] = useState(undefined);

  const getAddressKey = (item) =>
    [item?.address1, item?.address2, item?.city, item?.state, item?.zipcode]
      ?.map((part) => (part ? part?.trim()?.toLowerCase() : ""))
      ?.join("|");

  const normalizeAddressItem = (item) => ({
    ...item,
    address1: item?.address1
      ? Utils?.getStreetAddress({
          address: item?.address1,
          city: item?.city,
          state: item?.state,
          zipcode: item?.zipcode,
        })
      : "",
  });

  const addresses = [
    {
      id: "current",
      address1: patient?.address ? patient?.address : "",
      state: patient?.state ? patient?.state : "",
      city: patient?.city ? patient?.city : "",
      zipcode: patient?.zipcode ? patient?.zipcode : "",
      address2: patient?.address2 ? patient?.address2 : "",
    },
    ...(shipping_addresses || []),
  ]
    ?.map(normalizeAddressItem)
    ?.filter(
      (item, index, list) =>
        index ===
        list?.findIndex((el) => getAddressKey(el) === getAddressKey(item))
    );

  const updateShippingAddress = (params) => {
    if (params?.addressId !== "current") {
      dispatch(
        patchPatientShippingAddress({
          id: params?.id,
          addressId: params?.addressId,
          default: params.default,
        })
      ).then((res) => {
        if (res.type === "patch_patient_shiping_address/fulfilled") {
          setSelectedShippingAddress(initialAddress);
          dispatch(getPatientDetails({ id: params?.id }));
          setIsOnEditMode(false);
        } else if (res.type === "patch_patient_shiping_address/rejected") {
          const error = res?.payload;
          if (!Array.isArray(error) && typeof error === "object") {
            for (let key in error) {
              openNotificationWithIcon("error", key, error[key]);
              break;
            }
          } else {
            openNotificationWithIcon(
              "error",
              "Update Shipping address",
              "Shipping address did not changed"
            );
          }
        }
      });
    } else {
      setIsOnEditMode(false);
    }
  };

  useEffect(() => {
    setInitialAddress({
      id: "current",
      address1: patient?.address
        ? Utils?.getStreetAddress({
            address: patient?.address,
            city: patient?.city,
            state: patient?.state,
            zipcode: patient?.zipcode,
          })
        : "",
      state: patient?.state ? patient?.state : "",
      city: patient?.city ? patient?.city : "",
      zipcode: patient?.zipcode ? patient?.zipcode : "",
      address2: patient?.address2 ? patient?.address2 : "",
    });
  }, [
    patient?.address,
    patient?.address2,
    patient?.state,
    patient?.city,
    patient?.zipcode,
  ]);

  return (
    <div className="cartOrderDetails-cont__shippingAddressCont">
      <div className="cartOrderDetails-cont__textWrapper">
        <Typography.Text
          className="cartOrderDetails-cont__text"
          style={{
            color: token?.Typography?.mainColorText,
          }}>
          Shipping Address
        </Typography.Text>
        {isOnEditMode ? (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              type="text"
              size="small"
              disabled={!initialAddress || loading || error}
              loading={loading}
              icon={
                <CheckCircleOutlined
                  style={{
                    fontSize: "18px",
                    color: token?.icons?.colorPrimaryIcon,
                  }}
                />
              }
              onClick={() => {
                updateShippingAddress({
                  id: patient.id,
                  addressId: initialAddress?.id,
                  default: true,
                });
              }}
            />
            <Button
              type="text"
              size="small"
              disabled={loading}
              icon={
                <CloseCircleOutlined
                  style={{
                    fontSize: "18px",
                    color: token?.icons?.whiteIcon,
                  }}
                />
              }
              onClick={() => {
                if (error) {
                  dispatch(resetPatientError());
                }
                setIsOnEditMode(false);
                if (selectedShippingAddress) {
                  setInitialAddress(selectedShippingAddress);
                } else {
                  setInitialAddress({
                    id: "current",
                    address1: patient?.address
                      ? Utils?.getStreetAddress({
                          address: patient?.address,
                          city: patient?.city,
                          state: patient?.state,
                          zipcode: patient?.zipcode,
                        })
                      : "",
                    state: patient?.state ? patient?.state : "",
                    city: patient?.city ? patient?.city : "",
                    zipcode: patient?.zipcode ? patient?.zipcode : "",
                    address2: patient?.address2 ? patient?.address2 : "",
                  });
                }
              }}
            />
          </div>
        ) : (
          <Button
            type="text"
            size="small"
            loading={loading}
            icon={
              patient?.address ? (
                <EditOutlined
                  style={{
                    fontSize: "18px",
                    color: token?.icons?.colorPrimaryIcon,
                  }}
                />
              ) : (
                <PlusOutlined
                  style={{
                    fontSize: "18px",
                    color: token?.icons?.colorPrimaryIcon,
                  }}
                />
              )
            }
            onClick={() => {
              if (patient?.address) {
                if (error) {
                  dispatch(resetPatientError());
                }
                setIsOnEditMode(true);
              } else {
                if (error) {
                  dispatch(resetPatientError());
                }
                set_pick_up_point_opened(true);
              }
            }}
          />
        )}
      </div>
      {isOnEditMode ? (
        <ShippingSelect
          className="cartOrderDetails-cont__shippingAddressCont__shippingSelect"
          token={token}
          placeholder={"Shipping Address"}
          addBtnText="Add"
          loading={loading}
          options={addresses}
          selectedItem={initialAddress}
          setSelectedItem={setInitialAddress}
          addItem={(details) => {
            dispatch(
              postPatientShippingAddress({
                ...details,
                id: patient?.id,
              })
            ).then((res) => {
              if (res.type === "post_patient_shiping_address/rejected") {
                let error = res?.payload;
                for (let key in error) {
                  openNotificationWithIcon("error", key, error[key]);
                }
                setTimeout(() => {
                  dispatch(resetPatientError());
                }, 2000);
              }
            });
          }}
          error={error}
          resetError={() => {
            dispatch(resetPatientError());
          }}
        />
      ) : addressError ? (
        <Typography.Text
          className="cartOrderDetails-cont__shippingAddressCont__text"
          style={{
            color: token?.colorError,
          }}>
          {addressError}
        </Typography.Text>
      ) : (
        <Typography.Text className="cartOrderDetails-cont__shippingAddressCont__text">
          {selectedShippingAddress
            ? Utils?.getDisplayAddress(selectedShippingAddress)
            : Utils?.getDisplayAddress({
                address1: patient?.address,
                address2: patient?.address2,
                city: patient?.city,
                state: patient?.state,
                zipcode: patient?.zipcode,
              })}
        </Typography.Text>
      )}
    </div>
  );
}
